import { Link } from "react-router-dom";
import { ChevronRight, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";

const UPDATED = "June 2026";

function Block({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight mb-3">{title}</h2>
      <div className="space-y-3 text-base text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <>
      <Seo
        title="Privacy Policy | Ecroply AI"
        description="How Ecroply AI handles your data: PDF and image tools run entirely in your browser, AI tools send only the text you type, and we explain cookies and analytics."
        path="/privacy-policy"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Privacy Policy", path: "/privacy-policy" }]}
      />

      <section className="relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 grid-dots opacity-10" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
          <nav className="flex items-center gap-2 text-xs text-slate-400 mb-6" data-testid="breadcrumb">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-200">Privacy Policy</span>
          </nav>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center shrink-0">
              <ShieldCheck className="w-7 h-7 text-blue-300" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Privacy Policy</h1>
              <p className="mt-3 text-base md:text-lg text-slate-300 leading-relaxed">Last updated: {UPDATED}</p>
            </div>
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="privacy-content">
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-start gap-3">
          <ShieldCheck className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
          <p className="text-sm text-slate-700 leading-relaxed">
            <strong className="text-slate-900">The short version:</strong> Ecroply AI has no accounts or logins. Our PDF and image tools run entirely in your browser and never upload your files. The AI listing tools are the only exception — the text you type there is sent to an AI service to generate your result.
          </p>
        </div>

        <Block title="1. Who we are">
          <p>Ecroply AI ("we", "us", "our") provides free, browser-based tools for e-commerce sellers — shipping label croppers, PDF and image utilities, and AI listing generators — at ecroplyai.in. This policy explains what data is and isn't processed when you use the site.</p>
        </Block>

        <Block title="2. No accounts, no sign-up">
          <p>You don't need to create an account, log in, or provide a name, email or payment details to use any tool. We do not ask for or store personal profiles.</p>
        </Block>

        <Block title="3. Your files stay on your device">
          <p>The shipping label croppers, PDF tools (merge, rearrange, invoice cropper) and the image resizers run entirely inside your web browser using client-side processing. Your PDFs and images are never uploaded to our servers and are not stored by us. When you close the tab, the files are gone from the page.</p>
        </Block>

        <Block title="4. AI listing tools">
          <p>The AI listing tools (title, description, bullet point, SEO keyword and listing assistant generators) are the one exception to the "in your browser" rule. When you press Generate, the text you enter is sent to a third-party AI service to produce your result. This is stated clearly on each AI tool page.</p>
          <p>We use only the text you type to generate the output and return it to you. We don't ask you to include personal or sensitive information in these fields, and we recommend you don't. The input is used to fulfil that single request and isn't used to build a profile of you.</p>
        </Block>

        <Block title="5. Cookies and advertising (Google AdSense)">
          <p>We use Google AdSense to show ads, which keeps the tools free. Google and its partners may use cookies or similar technologies to serve and measure ads, including personalised ads based on your prior visits to this and other websites.</p>
          <p>You can control ad personalisation in your Google Ads Settings at <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">google.com/settings/ads</a>, and learn how Google uses data at <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">policies.google.com/technologies/partner-sites</a>. You can also opt out of third-party vendor cookies at <a href="https://www.aboutads.info" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline">aboutads.info</a>.</p>
        </Block>

        <Block title="6. Analytics">
          <p>We use privacy-conscious product analytics to understand which tools are used and to improve the site. This captures aggregate usage events (such as page views and interactions) and does not require you to identify yourself.</p>
        </Block>

        <Block title="7. Data we do not collect">
          <p>We do not sell your data, we do not store your uploaded files, and we do not maintain user accounts or marketing databases.</p>
        </Block>

        <Block title="8. Children's privacy">
          <p>Ecroply AI is a business tool intended for sellers and is not directed at children under 13. We do not knowingly collect personal information from children.</p>
        </Block>

        <Block title="9. Changes to this policy">
          <p>We may update this policy as the tools evolve or as legal requirements change. The "Last updated" date at the top will always reflect the current version.</p>
        </Block>

        <Block title="10. Contact us">
          <p>Questions about this policy or your data? Email us at <a href="mailto:support@ecroply.ai" className="text-blue-600 font-semibold hover:underline">support@ecroply.ai</a>.</p>
        </Block>
      </article>
    </>
  );
}
